import { PackageEvidence } from '@cadstrom/cadstrom-shared';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { useMemo } from 'react';

import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { applyViewerVote } from './apply-viewer-vote.js';

import type { Mutation } from '@tanstack/react-query';

/** Identifies one selection's row evidence: some rows of one package. */
export interface LinkVoteSelectionArgs {
	/** The package the selected rows belong to. */
	packageId: string;
	/** The selected rows, in selection order. */
	selections: PackageEvidence.EvidenceRowSelection[];
}

/**
 * The vote a castLinkVote mutation carries, or undefined when its variables are not a vote.
 *
 * @param mutation - A mutation from the mutation cache.
 * @returns The vote input, or undefined.
 */
function linkVoteOf(mutation: Mutation): PackageEvidence.LinkVoteInput | undefined {
	return PackageEvidence.LinkVoteInputSchema.safeParse(mutation.state.variables).data;
}

/**
 * Whether a vote was cast on the given selection's evidence.
 *
 * @param vote - The vote, or undefined for a mutation that is not one.
 * @param selection - The selection.
 * @returns True when the vote names the same package and the same rows in the same order.
 */
function isOnSelection(vote: PackageEvidence.LinkVoteInput | undefined, selection: LinkVoteSelectionArgs): boolean {
	return vote?.packageId === selection.packageId && isEqual(vote.selections, selection.selections);
}

/**
 * Casts the viewer's link votes on the evidence for a selection of package rows; each vote stands for every row of the
 * selection. The selection's evidence list shows the new stance as soon as a vote is cast, leaving the verdict to the
 * server; if the vote fails, only the voted locator reverts and the viewer is told. Votes on different locators may be
 * in flight together without disturbing one another's shown stance. Each vote updates the list for its own selection,
 * even if the viewer has selected other rows by the time it settles. Since selections that share a locator share the
 * votes on it, every list of the package is re-read from the server once no vote on the package remains in flight.
 *
 * @returns The vote mutation; its variables are the `castLinkVote` input.
 */
export function useCastLinkVote() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();
	const mutationKey = trpc.packageEvidence.castLinkVote.mutationKey();
	const listQueryKeyOf = ({ packageId, selections }: LinkVoteSelectionArgs) =>
		trpc.packageEvidence.rowEvidence.queryKey({ packageId, selections });
	const packageListsQueryKeyOf = ({ packageId }: LinkVoteSelectionArgs) =>
		trpc.packageEvidence.rowEvidence.queryKey({ packageId });
	const replaceEntry = (
		vote: PackageEvidence.LinkVoteInput,
		replace: (entry: PackageEvidence.RowEvidenceLocator) => PackageEvidence.RowEvidenceLocator,
	) =>
		queryClient.setQueryData(
			listQueryKeyOf(vote),
			(current) =>
				current && {
					locators: current.locators.map((entry) =>
						entry.locator.address === vote.locatorAddress ? replace(entry) : entry,
					),
				},
		);

	return useMutation(
		trpc.packageEvidence.castLinkVote.mutationOptions({
			onMutate: async (vote) => {
				await queryClient.cancelQueries({ queryKey: listQueryKeyOf(vote) });
				const previous = queryClient
					.getQueryData(listQueryKeyOf(vote))
					?.locators.find((entry) => entry.locator.address === vote.locatorAddress);
				replaceEntry(vote, (entry) => applyViewerVote(entry, vote.polarity));
				return { previous };
			},
			onError: (_error, vote, onMutateResult) => {
				const previous = onMutateResult?.previous;
				if (previous !== undefined) {
					replaceEntry(vote, () => previous);
				}
				addToast(<Toast severity='error' title='Could not save your vote' />);
			},
			onSuccess: ({ locator: updated }, vote) => {
				// A locator left with no live link to the selection drops out of the list when it is re-read.
				if (updated !== null) {
					replaceEntry(vote, () => updated);
				}
			},
			onSettled: async (_data, _error, vote) => {
				// This vote still counts as in flight here, so 1 means no other vote on the package is.
				const inFlightOnPackage = queryClient.isMutating({
					mutationKey,
					predicate: (mutation) => linkVoteOf(mutation)?.packageId === vote.packageId,
				});
				if (inFlightOnPackage <= 1) {
					await queryClient.invalidateQueries({ queryKey: packageListsQueryKeyOf(vote) });
				}
			},
		}),
	);
}

/**
 * The locators of one selection's evidence list whose vote is still being saved.
 *
 * @param selection - The package and rows whose pending votes to report.
 * @returns The evidence addresses with a vote in flight.
 */
export function usePendingLinkVoteAddresses({ packageId, selections }: LinkVoteSelectionArgs): ReadonlySet<string> {
	const trpc = useTRPC();
	const pendingVotes = useMutationState({
		filters: { mutationKey: trpc.packageEvidence.castLinkVote.mutationKey(), status: 'pending' },
		select: linkVoteOf,
	});
	return useMemo(
		() =>
			new Set(
				pendingVotes.flatMap((vote) =>
					vote !== undefined && isOnSelection(vote, { packageId, selections }) ? [vote.locatorAddress] : [],
				),
			),
		[pendingVotes, packageId, selections],
	);
}
